import React, { useContext, useEffect, useState } from 'react';
import { IRoute, ISubRoute, routeIconNames } from 'modules/Layouts/routes';
import { Layout, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { AppContext, IAppContext } from 'appState/appContext';
import { Icon, IconName } from 'components/Icon/Icon';
import classNames from 'classnames';
import style from './SidebarNew.module.scss';

const { Sider } = Layout;

interface ISidebar {
	routes: IRoute[];
	isHidden?: boolean;
}

const LOCAL_STORAGE_COLLAPSED_KEY = 'sidebarCollapsed';

const SidebarNew: React.FC<ISidebar> = ({ routes, isHidden }) => {
	const statusFromLocalStorage = localStorage.getItem(LOCAL_STORAGE_COLLAPSED_KEY);
	const defaultCollapsedStatus = statusFromLocalStorage ? statusFromLocalStorage === 'true' : true;

	const { loggedUser } = useContext<IAppContext>(AppContext);
	const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsedStatus);
	const { t: tRoutes } = useTranslation('routes');
	const { pathname: currentPath } = useLocation();

	const collapseIconClasses = classNames(style.collapseIcon, { [style.collapsed]: collapsed });
	const collapseContainerClasses = classNames(style.collapseContainer, { [style.collapsed]: collapsed });
	const collapseTitleClasses = classNames(style.collapseTitle, { [style.collapsed]: collapsed });

	const dividedPaths = currentPath.slice(1).split('/');
	const mainRoutePath = `/${dividedPaths[0]}`;
	const subRoutePath = `/${dividedPaths[0]}/${dividedPaths[1]}`;

	const [openedSubMenu, setOpenedSubMenu] = useState<string[]>([mainRoutePath]);

	useEffect(() => {
		setOpenedSubMenu([mainRoutePath]);
	}, [collapsed]);

	const toggleCollapsed = () => {
		setCollapsed((prevState) => !prevState);
		localStorage.setItem(LOCAL_STORAGE_COLLAPSED_KEY, collapsed ? 'false' : 'true');
	};

	// Open submenu after click on collapsed submenu item
	const [selectedSubMenu, setSelectedSubMenu] = useState<string>('');

	useEffect(() => {
		!collapsed && selectedSubMenu && setOpenedSubMenu([selectedSubMenu]);
		setSelectedSubMenu('');
	}, [collapsed, selectedSubMenu]);

	const uncollapseMenu = (titleKey: { key: string }) => {
		if (collapsed) {
			setSelectedSubMenu(titleKey.key);
			toggleCollapsed();
		}
	};

	// Only one sub menu dropdown should be opened
	// https://3x.ant.design/components/menu/#components-menu-demo-sider-current
	const handleOpenChange = (openKeys: string[]) => {
		const latestOpenKey = openKeys.find((key) => openedSubMenu.indexOf(key) === -1)!;
		const rootSubmenuKeys = routes.filter((el) => el.subroutes.length > 0).map((el) => el.path);
		if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
			setOpenedSubMenu(openKeys);
		} else {
			setOpenedSubMenu(latestOpenKey ? [latestOpenKey] : []);
		}
	};

	const renderMenuSingleItem = (item: IRoute, index: number) => {
		const isHidden = loggedUser.role ? !item.permissions.includes(loggedUser.role) : true;

		const itemClasses = classNames(style.menuItem, {
			[style.selected]: mainRoutePath === item.path,
			[style.adminSettings]: item.name === 'adminSettings',
		});
		const iconClasses = classNames(style.menuItemIcon, {
			[style.selected]: mainRoutePath === item.path,
		});
		const linkClasses = classNames(style.link, {
			[style.selected]: mainRoutePath === item.path,
		});
		const menuItemTitleClasses = classNames(style.menuItemTitle, {
			[style.collapsed]: collapsed,
		});

		if (isHidden) {
			return null;
		}

		return (
			<Menu.Item
				key={item.path}
				className={itemClasses}
				icon={<Icon name={routeIconNames[item.name]} className={iconClasses} size="xxl" />}
				data-test={`sidebar-item-${index}`}>
				<Link to={item.path} className={linkClasses}>
					<span className={menuItemTitleClasses}>{tRoutes(item.name)}</span>
				</Link>
			</Menu.Item>
		);
	};

	const renderSubmenuItem = (item: ISubRoute, index: number) => {
		const isHidden = loggedUser.role ? !item.permissions.includes(loggedUser.role) : true;

		const itemClasses = classNames(style.subMenuItem, {
			[style.selected]: subRoutePath === item.path,
		});
		const iconClasses = classNames(style.subMenuItemIcon, {
			[style.selected]: subRoutePath === item.path,
		});
		const linkClasses = classNames(style.link, {
			[style.selected]: subRoutePath === item.path,
		});

		if (isHidden) {
			return null;
		}

		return (
			<Menu.Item
				key={item.path}
				className={itemClasses}
				icon={<div className={iconClasses} />}
				data-test={`sidebar-item-of-dropdown-${item.path}-${index}`}>
				<Link to={item.path} className={linkClasses}>
					{tRoutes(item.name)}
				</Link>
			</Menu.Item>
		);
	};

	const renderSubMenu = (item: IRoute, index: number) => {
		const isHidden = loggedUser.role ? !item.permissions.includes(loggedUser.role) : true;
		const hasActiveChild = item.subroutes.some((sub) => sub.path === subRoutePath);

		const menuClasses = classNames(style.subMenu, {
			[style.selected]: mainRoutePath === item.path,
			[style.hasActiveChild]: hasActiveChild,
			[style.collapsed]: collapsed,
		});
		const iconClasses = style.subMenuIcon;
		const arrowClasses = classNames(style.arrow, {
			[style.arrowActive]: !!openedSubMenu.find((el) => el === item.path),
		});

		if (isHidden) {
			return null;
		}

		return (
			<Menu.SubMenu
				key={item.path}
				className={menuClasses}
				popupClassName={style.subMenuPopup}
				title={
					!collapsed ? (
						<div className={style.subMenuTitle}>
							{tRoutes(item.name)}
							<Icon name={IconName.arrowDropDown} className={arrowClasses} size="xl" />
						</div>
					) : (
						''
					)
				}
				onTitleClick={(e) => uncollapseMenu(e)}
				icon={
					<div className={style.subMenuIconContainer}>
						<Icon name={routeIconNames[item.name]} className={iconClasses} size="xxl" />
					</div>
				}
				data-test={`sidebar-item-${index}`}>
				{item.subroutes.map((el, subrouteIndex) => renderSubmenuItem(el, subrouteIndex))}
			</Menu.SubMenu>
		);
	};

	return !isHidden ? (
		<Sider
			className={style.sider}
			trigger={null}
			collapsible={true}
			collapsed={collapsed}
			collapsedWidth={88}
			width={344}>
			<Menu
				className={style.menu}
				mode="inline"
				selectable={false}
				_internalDisableMenuItemTitleTooltip={true}
				openKeys={openedSubMenu}
				onOpenChange={handleOpenChange}>
				<Menu.Item
					key="collapse"
					className={collapseContainerClasses}
					onClick={toggleCollapsed}
					icon={<Icon name={IconName.arrowLeft} className={collapseIconClasses} size="xxl" />}
					data-test="sidebar-collapsed-btn">
					<span className={collapseTitleClasses}>{tRoutes('hideMenu')}</span>
				</Menu.Item>
				<Menu.Divider className={style.divider} />
				{routes.map((el, index) =>
					el.subroutes.length ? renderSubMenu(el, index) : renderMenuSingleItem(el, index),
				)}
			</Menu>
		</Sider>
	) : null;
};

export default SidebarNew;
